import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { ListFilter } from "lucide-react";
import { Badge, CopyableText } from "components/common";
import { Skeleton } from "components/common/skeleton";
import { TableColumnFilter } from "components/common/PaginatedTableView/TableColumnFilter";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "components/common/dropdown/DropDownV2";
import type { LoginActivityTableProps } from "./LoginActivityTable.types";
import { getLoginStatusBadgeVariant } from "../loginActivity.utils";
import { parseDeviceSummary } from "utils/deviceSummary";
import { COLUMN_FILTER_CONFIGS } from "./loginActivityFilters";

dayjs.extend(relativeTime);

const LoginActivityTableSkeletonRow = () => (
  <tr className="hover:bg-muted/50 transition-colors">
    <td className="px-6 py-3 whitespace-nowrap">
      <div className="flex flex-col gap-0.5">
        <Skeleton className="h-3.5 w-24" />
        <Skeleton className="h-3 w-16" />
      </div>
    </td>
    <td className="px-6 py-3 whitespace-nowrap">
      <Skeleton className="h-5 w-16 rounded-md" />
    </td>
    <td className="px-6 py-3 whitespace-nowrap">
      <div className="flex flex-col gap-0.5">
        <Skeleton className="h-3.5 w-20" />
        <Skeleton className="h-3 w-12" />
      </div>
    </td>
    <td className="px-6 py-3 whitespace-nowrap">
      <Skeleton className="h-3.5 w-12" />
    </td>
    <td className="px-6 py-3 whitespace-nowrap">
      <Skeleton className="h-3.5 w-20" />
    </td>
    <td className="px-6 py-3">
      <div className="flex items-center gap-2">
        <Skeleton className="w-4 h-4 rounded" />
        <Skeleton className="h-3.5 w-24" />
      </div>
    </td>
  </tr>
);

export const LoginActivityTable = ({
  rows,
  isLoading = false,
  skeletonRowCount = 10,
  filters = {},
  onFiltersChange,
  accent = "gray",
  activeSessionIds,
}: LoginActivityTableProps) => {
  const handleFilterChange = (columnKey: string, value: string | string[] | undefined) => {
    if (!onFiltersChange) return;
    const newFilters = { ...filters };
    if (
      value === undefined ||
      (Array.isArray(value) && value.length === 0) ||
      (typeof value === "string" && value.trim() === "")
    ) {
      delete newFilters[columnKey];
    } else {
      newFilters[columnKey] = value;
    }
    onFiltersChange(newFilters);
  };

  const getFilterConfig = (columnKey: string) => {
    return COLUMN_FILTER_CONFIGS.find((config) => config.columnKey === columnKey);
  };

  const isFilterActive = (columnKey: string): boolean => {
    const value = filters[columnKey];
    if (Array.isArray(value)) {
      return value.length > 0;
    }
    if (typeof value === "string") {
      return value.trim().length > 0;
    }
    return false;
  };

  const renderFilterIcon = (columnKey: string) => {
    if (!onFiltersChange) return null;
    const config = getFilterConfig(columnKey);
    if (!config) return null;

    const active = isFilterActive(columnKey);

    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            className={`inline-flex items-center justify-center h-5 w-5 rounded transition-colors ${
              active
                ? "text-foreground bg-muted hover:bg-muted"
                : "text-muted-foreground/70 bg-muted/60 hover:bg-muted hover:text-muted-foreground"
            }`}
            type="button"
          >
            <ListFilter className="h-3.5 w-3.5" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-64 p-3">
          <TableColumnFilter
            config={config}
            value={filters[columnKey]}
            onChange={(value) => handleFilterChange(columnKey, value)}
            accent={accent}
          />
        </DropdownMenuContent>
      </DropdownMenu>
    );
  };

  return (
    <table className="w-full">
      <thead className="bg-card border-b border-border sticky top-0 z-10">
        <tr>
          <th className="px-6 py-3.5 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">
            Date & Time
          </th>
          <th className="px-6 py-3.5 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">
            <div className="flex items-center gap-2">
              <span>Status</span>
              {renderFilterIcon("status")}
            </div>
          </th>
          <th className="px-6 py-3.5 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">
            <div className="flex items-center gap-2">
              <span>Method</span>
              {renderFilterIcon("method")}
            </div>
          </th>
          <th className="px-6 py-3.5 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">
            Location
          </th>
          <th className="px-6 py-3.5 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">
            IP Address
          </th>
          <th className="px-6 py-3.5 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">
            Device
          </th>
        </tr>
      </thead>
      <tbody className="divide-y divide-border">
        {isLoading ? (
          Array.from({ length: skeletonRowCount }, (_, i) => (
            <LoginActivityTableSkeletonRow key={`skeleton-${i}`} />
          ))
        ) : rows.length === 0 ? (
          <tr>
            <td colSpan={6} className="px-6 py-12 text-center">
              <p className="text-sm text-muted-foreground">No results found</p>
            </td>
          </tr>
        ) : (
          rows.map((row) => (
            <tr key={row.login_audit_id} className="hover:bg-muted/50 transition-colors group">
              <td className="px-6 py-3 whitespace-nowrap">
                <CopyableText
                  copyValue={row.created_at ? new Date(row.created_at).toISOString() : ""}
                  className="flex flex-col gap-0.5"
                  buttonClassName="flex flex-col"
                  showCopyButton={false}
                  align="left"
                >
                  <span className="text-xs text-foreground/80 font-medium">
                    {dayjs(row.created_at).format("MMM D, YYYY")}
                  </span>
                  <span className="text-xs text-muted-foreground font-mono">
                    {dayjs(row.created_at).format("HH:mm:ss")}
                  </span>
                </CopyableText>
              </td>
              <td className="px-6 py-3 whitespace-nowrap">
                <div className="flex items-center gap-2">
                  <Badge variant={getLoginStatusBadgeVariant(row.status)} size="sm" rounded="md">
                    {row.status}
                  </Badge>
                  {row.session_id && activeSessionIds?.has(row.session_id) ? (
                    <Badge variant="emerald" size="sm" rounded="md">
                      Active
                    </Badge>
                  ) : null}
                </div>
              </td>
              <td className="px-6 py-3 whitespace-nowrap">
                <div className="flex flex-col gap-0.5">
                  {row.status === "LOGOUT" ? (
                    <span className="text-sm text-foreground/90">Logout</span>
                  ) : (
                    <span className="text-sm text-foreground/90">{row.login_method}</span>
                  )}
                  {row.mfa_required && row.status !== "LOGOUT" && (
                    <span className="text-xs text-muted-foreground">
                      MFA{row.mfa_method ? ` (${row.mfa_method})` : ""}
                    </span>
                  )}
                  {row.status === "LOGOUT" && (
                    <span className="text-xs text-muted-foreground">
                      Session: {row.login_method}
                    </span>
                  )}
                </div>
              </td>
              <td className="px-6 py-3 whitespace-nowrap">
                <span className="text-sm text-foreground/90">
                  {row.location_label || row.country_code || "—"}
                </span>
              </td>
              <td className="px-6 py-3 whitespace-nowrap">
                {row.ip_address_masked ? (
                  <CopyableText
                    text={row.ip_address_masked}
                    copyValue={row.ip_address_masked}
                    className="text-sm font-mono"
                    showCopyButton={true}
                  />
                ) : row.is_local_network ? (
                  <span className="text-sm text-muted-foreground/70">Local network</span>
                ) : (
                  <span className="text-sm text-muted-foreground/70">—</span>
                )}
              </td>
              <td className="px-6 py-3">
                {(() => {
                  const deviceInfo = parseDeviceSummary(row.device_summary);
                  if (!deviceInfo) {
                    return <span className="text-sm text-muted-foreground/70">—</span>;
                  }
                  const { IconComponent, name } = deviceInfo;
                  return (
                    <div className="flex items-center gap-2">
                      <IconComponent className="w-4 h-4 text-muted-foreground" />
                      <span className="text-sm text-foreground/90">{name}</span>
                    </div>
                  );
                })()}
              </td>
            </tr>
          ))
        )}
      </tbody>
    </table>
  );
};
